import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthenticatedBaseQuery, API_BASE_URLS } from '../shared';
import type {
  MedNecCaseView,
  CaseDocumentsView,
  DocumentUrlView,
  DashboardSummaryView,
  IngestionResultView,
  GapAnalysisStatusView,
} from './types/medNecTypes';
import type { CaseListRequest } from './requests/caseListRequest';
import type { DocumentUploadRequest } from './requests/documentUploadRequest';
import type { DocumentRemoveRequest } from './requests/documentRemoveRequest';
import type { DocumentTagRequest } from './requests/documentRetagRequest';

export const medNecApi = createApi({
  reducerPath: 'medNecApi',
  baseQuery: createAuthenticatedBaseQuery(API_BASE_URLS.medNec),
  tagTypes: ['MedNecCase', 'MedNecCaseList', 'MedNecDocument', 'MedNecDashboard', 'MedNecGapAnalysis'],
  endpoints: (builder) => ({
    // ── Case ─────────────────────────────────────────────────────────

    getCaseById: builder.query<MedNecCaseView, string>({
      query: (id) => ({
        url: 'MedNecCase',
        params: { id },
      }),
      providesTags: (result, error, id) => [{ type: 'MedNecCase', id }],
    }),

    getCases: builder.query<MedNecCaseView[], CaseListRequest>({
      query: ({ status, facility } = {}) => ({
        url: 'MedNecCase/list',
        params: {
          ...(status ? { status } : {}),
          ...(facility ? { facility } : {}),
        },
      }),
      providesTags: ['MedNecCaseList'],
    }),

    getCaseByEncounter: builder.query<MedNecCaseView, string>({
      query: (encounterId) => ({
        url: 'MedNecCase/by-encounter',
        params: { encounterId },
      }),
      providesTags: (result) =>
        result ? [{ type: 'MedNecCase', id: result.id }] : [],
    }),

    updateCase: builder.mutation<void, { id: string; body: MedNecCaseView }>({
      query: ({ id, body }) => ({
        url: 'MedNecCase',
        method: 'PATCH',
        params: { id },
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MedNecCase', id },
        'MedNecCaseList',
        'MedNecDashboard',
      ],
    }),

    createCase: builder.mutation<MedNecCaseView, { view: Partial<MedNecCaseView> }>({
      query: (body) => ({
        url: 'MedNecCase',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MedNecCaseList', 'MedNecDashboard'],
    }),

    // ── Documents ────────────────────────────────────────────────────

    getCaseDocuments: builder.query<CaseDocumentsView, string>({
      query: (id) => ({
        url: 'MedNecCase/documents',
        params: { id },
      }),
      providesTags: (result, error, id) => [{ type: 'MedNecDocument', id }],
    }),

    getDocumentUrl: builder.query<DocumentUrlView, { id: string; documentId: string }>({
      query: ({ id, documentId }) => ({
        url: 'MedNecCase/documents/url',
        params: { id, documentId },
      }),
    }),

    uploadDocument: builder.mutation<void, DocumentUploadRequest>({
      query: ({ caseId, documentTypes, file }) => {
        const formData = new FormData();
        formData.append('File', file);
        return {
          url: 'MedNecCase/documents',
          method: 'POST',
          params: { Id: caseId, DocumentTypes: documentTypes.join(',') },
          body: formData,
        };
      },
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'MedNecDocument', id: caseId },
        { type: 'MedNecCase', id: caseId },
        'MedNecCaseList',
      ],
    }),

    removeDocument: builder.mutation<void, DocumentRemoveRequest>({
      query: ({ id, documentId }) => ({
        url: 'MedNecCase/documents',
        method: 'DELETE',
        params: { Id: id, DocumentId: documentId },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MedNecDocument', id },
        { type: 'MedNecCase', id },
      ],
    }),

    addDocumentTag: builder.mutation<void, DocumentTagRequest>({
      query: ({ id, documentId, tag }) => ({
        url: 'MedNecCase/documents/tags/add',
        method: 'PATCH',
        params: { Id: id, DocumentId: documentId, Tag: tag },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MedNecDocument', id },
        { type: 'MedNecCase', id },
      ],
    }),

    removeDocumentTag: builder.mutation<void, DocumentTagRequest>({
      query: ({ id, documentId, tag }) => ({
        url: 'MedNecCase/documents/tags/remove',
        method: 'PATCH',
        params: { Id: id, DocumentId: documentId, Tag: tag },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MedNecDocument', id },
        { type: 'MedNecCase', id },
      ],
    }),

    // ── Dashboard ────────────────────────────────────────────────────

    getDashboardSummary: builder.query<DashboardSummaryView, string | undefined>({
      query: (facility) => ({
        url: 'MedNecCase/dashboard/summary',
        params: facility ? { facility } : {},
      }),
      providesTags: ['MedNecDashboard'],
    }),

    getDashboardQueue: builder.query<MedNecCaseView[], string>({
      query: (facility) => ({
        url: 'MedNecCase/dashboard/queue',
        params: { facility },
      }),
      providesTags: ['MedNecDashboard', 'MedNecCaseList'],
    }),

    // ── Ingestion ────────────────────────────────────────────────────

    ingestSchedulingPacket: builder.mutation<IngestionResultView, { facility: string; file: File }>({
      query: ({ facility, file }) => {
        const formData = new FormData();
        formData.append('File', file);
        return {
          url: 'MedNecCase/ingest',
          method: 'POST',
          params: { Facility: facility },
          body: formData,
        };
      },
      invalidatesTags: ['MedNecCaseList', 'MedNecDashboard'],
    }),

    // ── Gap Analysis ─────────────────────────────────────────────────

    getGapAnalysis: builder.query<GapAnalysisStatusView, string>({
      query: (id) => ({
        url: 'MedNecCase/gap-analysis',
        params: { id },
      }),
      providesTags: (result, error, id) => [{ type: 'MedNecGapAnalysis', id }],
    }),

    runGapAnalysis: builder.mutation<GapAnalysisStatusView, { id: string }>({
      query: ({ id }) => ({
        url: 'MedNecCase/gap-analysis',
        method: 'POST',
        params: { id },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'MedNecGapAnalysis', id },
        { type: 'MedNecCase', id },
      ],
    }),

    toggleGapAnalysisItem: builder.mutation<void, {
      id: string;
      section: 'preAuth' | 'actionItem' | 'criterionAction';
      index: number;
      criterionIndex?: number;
    }>({
      query: ({ id, section, index, criterionIndex }) => ({
        url: 'MedNecCase/gap-analysis/toggle',
        method: 'PATCH',
        params: { id },
        body: { section, index, criterionIndex },
      }),
      async onQueryStarted({ id, section, index, criterionIndex }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          medNecApi.util.updateQueryData('getGapAnalysis', id, (draft) => {
            const analysis = draft.analysis;
            if (!analysis) return;
            if (section === 'preAuth' && analysis.preAuthChecklist[index]) {
              analysis.preAuthChecklist[index].isChecked = !analysis.preAuthChecklist[index].isChecked;
            } else if (section === 'actionItem' && analysis.actionItemsSummary[index]) {
              analysis.actionItemsSummary[index].isChecked = !analysis.actionItemsSummary[index].isChecked;
            } else if (section === 'criterionAction' && criterionIndex !== undefined && analysis.criteria[criterionIndex]?.actionItems[index]) {
              analysis.criteria[criterionIndex].actionItems[index].isChecked = !analysis.criteria[criterionIndex].actionItems[index].isChecked;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    editGapCriterion: builder.mutation<void, {
      id: string;
      operation: 'updateStatus' | 'setComment' | 'addCriterion' | 'removeCriterion';
      index: number;
      status?: string;
      comment?: string;
      description?: string;
    }>({
      query: ({ id, operation, index, status, comment, description }) => ({
        url: 'MedNecCase/gap-analysis/criterion',
        method: 'PATCH',
        params: { id },
        body: { operation, index, status, comment, description },
      }),
      async onQueryStarted({ id, operation, index, status, comment, description }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          medNecApi.util.updateQueryData('getGapAnalysis', id, (draft) => {
            const analysis = draft.analysis;
            if (!analysis) return;
            switch (operation) {
              case 'updateStatus':
                if (analysis.criteria[index] && status) {
                  analysis.criteria[index].status = status;
                }
                break;
              case 'setComment':
                if (analysis.criteria[index]) {
                  analysis.criteria[index].nurseComment = comment ?? null;
                }
                break;
              case 'addCriterion':
                analysis.criteria.push({
                  description: description ?? '',
                  status: status ?? 'GAP',
                  confidence: 1,
                  policySectionReference: '',
                  evaluationReasoning: '',
                  evidenceQuotes: [],
                  gapComments: null,
                  actionItems: [],
                  subCriteria: [],
                  requiredDocuments: [],
                  nurseComment: null,
                  isManual: true,
                });
                break;
              case 'removeCriterion':
                if (index >= 0 && index < analysis.criteria.length) {
                  analysis.criteria.splice(index, 1);
                }
                break;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // ── Physician Query Letter ───────────────────────────────────────

    generateQueryLetter: builder.mutation<{ html: string }, {
      id: string;
      facility: {
        name: string;
        address1: string;
        address2?: string;
        city: string;
        state: string;
        zip: string;
        phone?: string;
        email?: string;
      };
    }>({
      query: ({ id, facility }) => ({
        url: 'MedNecCase/physician-query-letter',
        method: 'POST',
        params: { id },
        body: facility,
      }),
    }),

    downloadQueryLetterPdf: builder.mutation<Blob, {
      html: string;
      facility: {
        name: string;
        address1: string;
        address2?: string;
        city: string;
        state: string;
        zip: string;
        phone?: string;
        email?: string;
      };
      patientName: string;
      dateOfBirth: string;
      payerName: string;
      procedures: string;
      caseReference: string;
    }>({
      query: (body) => ({
        url: 'MedNecCase/physician-query-letter/pdf',
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // ── Letter of Medical Necessity (LOMN) ─────────────────────────────

    generateLomn: builder.mutation<{ html: string }, {
      id: string;
      facility: {
        name: string;
        address1: string;
        address2?: string;
        city: string;
        state: string;
        zip: string;
        phone?: string;
        email?: string;
      };
    }>({
      query: ({ id, facility }) => ({
        url: 'MedNecCase/lomn',
        method: 'POST',
        params: { id },
        body: facility,
      }),
    }),

    downloadLomnPdf: builder.mutation<Blob, {
      html: string;
      facility: {
        name: string;
        address1: string;
        address2?: string;
        city: string;
        state: string;
        zip: string;
        phone?: string;
        email?: string;
      };
      patientName: string;
      dateOfBirth: string;
      payerName: string;
      procedures: string;
      caseReference: string;
    }>({
      query: (body) => ({
        url: 'MedNecCase/lomn/pdf',
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    transitionCase: builder.mutation<void, { caseId: string; targetStatus: string; reason: string }>({
      query: ({ caseId, ...body }) => ({
        url: `MedNecCase/transition?id=${caseId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { caseId }) => [
        { type: 'MedNecCase', id: caseId },
        'MedNecCaseList',
        'MedNecDashboard',
      ],
    }),
  }),
});

export const {
  useGetCaseByIdQuery,
  useGetCasesQuery,
  useGetCaseByEncounterQuery,
  useUpdateCaseMutation,
  useGetCaseDocumentsQuery,
  useGetDocumentUrlQuery,
  useUploadDocumentMutation,
  useRemoveDocumentMutation,
  useAddDocumentTagMutation,
  useRemoveDocumentTagMutation,
  useGetDashboardSummaryQuery,
  useGetDashboardQueueQuery,
  useCreateCaseMutation,
  useIngestSchedulingPacketMutation,
  useGetGapAnalysisQuery,
  useRunGapAnalysisMutation,
  useToggleGapAnalysisItemMutation,
  useEditGapCriterionMutation,
  useGenerateQueryLetterMutation,
  useDownloadQueryLetterPdfMutation,
  useGenerateLomnMutation,
  useDownloadLomnPdfMutation,
  useTransitionCaseMutation,
} = medNecApi;
